import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import TextTruncate from "react-text-truncate";
import { isNil, isNull } from "lodash";
import { useQuery } from "@tanstack/react-query";
import { isZero } from "../../../../../helpers/helpers";
import moment from "moment-timezone";
import { formatPrice } from "../../../../../helpers/helpers";
import { getDisplayPrice } from "../../../../../helpers/helpers";

import SocialPropertyInfoImageSwiper from "./SocialPropertyInfoImageSwiper";
import LeafletMap from "../../../../../components/common/LeafletMap";

import { getSalesData } from "../../../../../services/PropertyService";

/**
 * SocialPropertyInfo component displays Property in-detail Information with Images with swiper on Listing Page
 * @param {Object} props - Component props
 * @param {Object} props.property - Properties Details
 * @returns {JSX.Element} - Property In-detail information with map and swiper.
 */
const SocialPropertyInfo = ({ property }) => {
    const [propertyType, setPropertyType] = useState();
    const [agentData, setAgentData] = useState();
    const [showMore, setShowMore] = useState(false);
    const [openSalesInfo, setOpenSalesInfo] = useState(false);

    const formatDate = (date) => {
        return date ? moment(date).format("MM/DD/YYYY") : "Not Yet";
    };

    const salesDataQuery = useQuery({
        queryKey: ["current_property_sales_data", property?.id],
        queryFn: () => getSalesData(property?.id),
        enabled: !isNil(property?.id) && !isNull(property?.id),
    });

    // Handle sales data success
    useEffect(() => {
        if (salesDataQuery.data) {
            setAgentData(salesDataQuery.data);
        }
    }, [salesDataQuery.data]);

    useEffect(() => {
        if (property.property_type === "Condominium") {
            setPropertyType("Condo");
        } else if (property.property_type === "Single Family Residential") {
            setPropertyType("House");
        } else {
            setPropertyType("Unknown");
        }
    }, [property]);

    const updatedDate = !isNil(agentData?.property?.updated_at)
        ? new Date(agentData.property.updated_at).toLocaleDateString()
        : "";

    const trimmedListingID = !isNil(agentData?.listingId) ? agentData.listingId.replace(/\D/g, "") : "";

    return (
        <div>
            <SocialPropertyInfoImageSwiper property={property} />
            <div className="property-details property-name tw-flex tw-flex-col tw-px-2">
                <div className="property-details-key">
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
                        <h2 className="tw-px-4 md:tw-px-0 tw-font-bold">
                            {formatPrice(getDisplayPrice(property))}
                        </h2>
                        <h3
                            className="tw-px-4 md:tw-px-0 !tw-text-gray-500 !tw-font-normal !tw-text-base md:!tw-text-xl"
                            id="address"
                        >
                            {property.street_address} | {property.neighborhood},{property.state}
                        </h3>
                    </div>

                    <div className="main-info-row px-0">
                        <div className="main-info-column bed-column">
                            <p>Beds</p>
                            <span id="bedrooms" className="tw-pb-2">
                                {property.bedrooms}
                            </span>
                        </div>
                        <div className="main-info-column bath-column">
                            <p>Baths</p>
                            <span id="bathrooms">{property.bathrooms}</span>
                        </div>
                        <div className="main-info-column sq-column">
                            <p>Sq. Ft</p>
                            <span id="square-footage">
                                {isZero(property.square_footage) ? "Unknown" : property.square_footage}
                            </span>
                        </div>
                        <div className="main-info-column type-column">
                            <p>Type</p>

                            <span id="property-type">{propertyType}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="property-map-container tw-px-0">
                <LeafletMap property={property} />
            </div>
            <div className="property-details about-listing-row !tw-px-5">
                <div className="px-0">
                    <h3 className="mt-2">
                        <strong className="subtitle">About this listing</strong>
                    </h3>
                    {!showMore ? (
                        <TextTruncate
                            line={3}
                            element="p"
                            truncateText="…"
                            text={property.public_remarks}
                            containerClassName="truncated-public-remarks-container"
                            textTruncateChild={
                                <button
                                    onClick={() => setShowMore(true)}
                                    className="tw-font-semibold tw-bg-white tw-text-black tw-border-none tw-p-0 tw-cursor-pointer"
                                >
                                    Show More
                                </button>
                            }
                        />
                    ) : (
                        <p>
                            <span className="public-remarks">{property.public_remarks}</span>{" "}
                            <span>
                                <button
                                    onClick={() => setShowMore(false)}
                                    className="tw-font-semibold tw-bg-white tw-text-black tw-border-none tw-p-0 tw-cursor-pointer"
                                >
                                    Show less
                                </button>
                            </span>
                        </p>
                    )}
                </div>
            </div>

            <div className="property-details" id="additional-details">
                <div className="!tw-px-5">
                    <h3 className="mb-2">
                        <strong className="subtitle">Listing History</strong>
                    </h3>
                    <div className="details-item d-flex justify-content-between">
                        <span>List Date:</span>
                        <p>{formatDate(property.list_date)}</p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>Market Time:</span>
                        <p>
                            {isZero(property.market_time)
                                ? "Not Yet"
                                : `${property.market_time} day${property.market_time === 1 ? "" : "s"}`}
                        </p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>Contract Date:</span>
                        <p>{formatDate(property.contract_date)}</p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>Sold Date:</span>
                        <p>{formatDate(property.close_date)}</p>
                    </div>
                    <h3 className="mb-2">
                        <strong className="subtitle">Additional Details</strong>
                    </h3>
                    <div className="details-item d-flex justify-content-between">
                        <span>Parking Spots:</span>
                        <p>{`${property.parking_spots} ${property.parking_type}`}</p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>Year Built:</span>
                        <p>{isZero(property.year_built) ? "Unknown" : property.year_built}</p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>Exterior Features:</span>
                        <p>{property.exterior_features}</p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>Interior Features:</span>
                        <p>{property.interior_features}</p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>Heating:</span>
                        <p>{property.heating}</p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>AC:</span>
                        <p>{property.cooling}</p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>Laundry Features:</span>
                        <p>{property.laundry_features}</p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>Lot Dimensions:</span>
                        <p>{property.lot_dimensions}</p>
                    </div>
                    <h3 className="mt-4 mb-2">
                        <strong className="subtitle">School Details</strong>
                    </h3>
                    <div className="details-item d-flex justify-content-between">
                        <span>Elementary School:</span>
                        <p>
                            {property.school?.elementarySchool
                                ? property.school.elementarySchool
                                : "Not reported"}
                        </p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>Middle School:</span>
                        <p>{property.school?.middleSchool ? property.school.middleSchool : "Not reported"}</p>
                    </div>
                    <div className="details-item d-flex justify-content-between">
                        <span>High School:</span>
                        <p>{property.school?.highSchool ? property.school.highSchool : "Not reported"}</p>
                    </div>
                    <div className="tw-flex tw-gap-x-2">
                        {(() => {
                            const emailString = agentData?.agent?.contact?.email || "";
                            const firstEmail = emailString.split(";")[0]?.trim();

                            return firstEmail ? (
                                <a
                                    href={`mailto:${firstEmail}`}
                                    className="tw-no-underline  tw-p-2 tw-rounded-md tw-w-full tw-bg-[#FF9D00] hover:tw-bg-[#ffae32] !tw-text-white tw-font-bold tw-text-xs md:tw-text-base tw-flex tw-items-center tw-justify-center"
                                >
                                    Contact Listing Agent
                                </a>
                            ) : (
                                <div
                                    disabled
                                    className="tw-p-1 tw-rounded-md tw-w-full tw-bg-gray-200 tw-text-gray-500 tw-font-bold !tw-border tw-border-solid tw-border-gray-300 cursor-not-allowed tw-text-xs md:tw-text-base tw-flex tw-items-center tw-justify-center"
                                >
                                    Contact Info Unavailable
                                </div>
                            );
                        })()}
                        <button
                            type="button"
                            className="tw-w-full tw-rounded-md tw-font-bold tw-text-[#F69929] tw-py-2 tw-bg-[#F69929] tw-bg-opacity-15"
                            onClick={() => setOpenSalesInfo(!openSalesInfo)}
                        >
                            Sale Information
                        </button>
                    </div>
                    <div className="sales-info-container">
                        {openSalesInfo && (
                            <>
                                {agentData && Object.keys(agentData).length > 0 ? (
                                    <>
                                        <h3 className="mb-2 tw-mt-4">
                                            <strong className="subtitle">Sales Info</strong>
                                        </h3>
                                        <div className="details-item d-flex justify-content-between">
                                            <span>Source:</span>
                                            <p>
                                                {agentData?.originatingSystemName} as Distributed by MLS Grid
                                                #{trimmedListingID}
                                            </p>
                                        </div>
                                        <div className="details-item d-flex justify-content-between">
                                            <span>Listing agent:</span>
                                            <p>
                                                {agentData?.agent?.firstName} {agentData?.agent?.lastName},{" "}
                                                {agentData?.office?.name} (
                                                {agentData?.office?.contact?.office})
                                            </p>
                                        </div>
                                        {property.status === "Closed" && (
                                            <div className="details-item d-flex justify-content-between">
                                                <span>Buyer's agent:</span>
                                                <p>
                                                    {agentData?.sales?.agent?.firstName}{" "}
                                                    {agentData?.sales?.agent?.lastName},{" "}
                                                    {agentData?.sales?.office?.servingName}
                                                </p>
                                            </div>
                                        )}
                                        <div>
                                            <p className="sales-info-disclaimer">
                                                Details provided by {agentData?.originatingSystemName} as
                                                Distributed by MLS Grid and may not match the public record.
                                                Based on information submitted to the MLS GRID as of{" "}
                                                {updatedDate} as licensed by Mike McElroy of Kale Realty. All
                                                data is obtained from various sources and may not have been
                                                verified by broker or MLS GRID. Supplied Open House
                                                Information is subject to change without notice. All
                                                information should be independently reviewed and verified for
                                                accuracy. Properties may or may not be listed by the
                                                office/agent presenting the information. Properties displayed
                                                may be listed or sold by various participants in the MLS. Some
                                                IDX listings have been excluded from this website.
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <div className="tw-text-gray-500 tw-font-medium tw-text-center tw-p-2">
                                        Sales Information not found.
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

SocialPropertyInfo.propTypes = {
    property: PropTypes.object.isRequired,
};

export default SocialPropertyInfo;
